/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client';
import { Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import {
    getDefaultWorkflowSchema,
    Mode,
    setTemplateInstance,
} from '../../../apollo/reactiveVariables/TemplateInstanceVariables';
import {
    currentScopeInCreationVar,
    currentTemplateVar,
    modalDataVar,
    resetPreSetupTemplateVars,
    TemplateScope,
} from '../../../apollo/reactiveVariables/TemplateManagementVariables';
import { Paths } from '../../../common/enums/Paths';
import { mapFormEntriesToQuestions, parseFormTemplate } from '../../../forms';
import { PanelRegion, PanelType, ScenarioName, StatusName, StepName, SubWorkLoad } from '../../../logger';
import { ApprovalTemplateInstance, ApprovalTemplateInstanceScopeType } from '../../../service/gen/templates';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { useIsAdmin } from '../../../utilities/TemplateManagementUtils';
import { Template } from '../../../utilities/TemplateUtils';
import { isSelfOwnedFormEnabled } from '../../../utilities/Utils';
import { LoadingPage } from '../../containers/LoadingPage';
import { getPivotStyles } from './PreSetupTemplateWizard.styles';
import { CreateAdminGroup } from './Steps/CreateAdminGroup';
import { PreSetupTemplateStepProps, PreSetupTemplateSteps } from './Steps/PreSetupTemplateSteps';
import { PreviewTemplate } from './Steps/PreviewTemplate';
import { TemplateStore } from './Steps/TemplateStore';
import { ViewScope } from './Steps/ViewScope';
import { ViewTeam } from './Steps/ViewTeam';

export interface PreSetupTemplateWizardProps {
    template?: Template;
}

export const PreSetupTemplateWizard: React.FC<PreSetupTemplateWizardProps> = (
    props: PreSetupTemplateWizardProps
): JSX.Element => {
    const host = getAppHost();
    const logger = host.getLogger();
    const { template } = props;
    const [currentStep, setCurrentStep] = React.useState<PreSetupTemplateSteps>(PreSetupTemplateSteps.None);
    const history = useHistory();
    const currentTemplate = useReactiveVar(currentTemplateVar);
    const currentScope = useReactiveVar(currentScopeInCreationVar);
    const isTemplatePreviewEnabled = getAppHost().getEcsConfig().MicrosoftTeamApprovals.TemplatePreviewEnabled;
    const isAdmin = useIsAdmin();

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    getAppHost().getLogger().useCreateScenarioEffect(
        ScenarioName.CreateTemplateInstance,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        getAppHost().getPersonalAppEntryPoint(),
        'templateManagementCommandBar',
        PanelRegion.Modal,
        PanelType.PreSetupTemplateInstance
    );

    React.useEffect((): void => {
        resetPreSetupTemplateVars();
        if (template) {
            currentTemplateVar(template);
            if (isTemplatePreviewEnabled) {
                setCurrentStep(PreSetupTemplateSteps.PreviewTemplate);
            } else {
                if (isAdmin) {
                    setCurrentStep(PreSetupTemplateSteps.SelectScope);
                } else {
                    setCurrentStep(PreSetupTemplateSteps.SelectTeam);
                }
            }
        } else {
            setCurrentStep(PreSetupTemplateSteps.BrowseTemplates);
        }
    }, [isAdmin]);

    const done = (): void => {
        logger.markScenario(ScenarioName.CreateTemplateInstance, StepName.OpenTemplateInstanceWizard, StatusName.NA);
        logger.logUserBiAction(
            SubWorkLoad.AppInteraction,
            ScenarioName.CreateTemplateInstance,
            StepName.OpenTemplateInstanceWizard,
            'click',
            'nav',
            'PrimaryButton',
            'carouselButton',
            PanelRegion.Modal,
            PanelType.PreSetupTemplateInstance
        );

        modalDataVar({ isOpen: false });
        let templateInstance: ApprovalTemplateInstance = null;
        // Derive props from template if it's not creating from scratch
        if (currentTemplate) {
            templateInstance = {
                Title: currentTemplate.title,
                Category: currentTemplate.category,
                Description: currentTemplate.description,
                AccentColor: currentTemplate.iconBackgroundColor,
                IconName: currentTemplate.iconName,
                IsEnabled: true,
                Scope:
                    currentScope === TemplateScope.Team
                        ? ApprovalTemplateInstanceScopeType.Team
                        : ApprovalTemplateInstanceScopeType.Organization,
                LastModifiedAt: new Date().toString(),
                IsVisibleToAll: currentScope === TemplateScope.TenantAll,
                WorkflowSchema: getDefaultWorkflowSchema(),
                ...(isSelfOwnedFormEnabled()
                    ? { Questions: mapFormEntriesToQuestions(parseFormTemplate(currentTemplate.formTemplate)) }
                    : { FormId: '' }),
            } as any;
        }
        setTemplateInstance(templateInstance, Mode.Create);
        history.push({
            pathname: Paths.BasePath + Paths.CreateTemplateInstance,
        });
    };

    const stepProps: PreSetupTemplateStepProps = {
        navigateTo: setCurrentStep,
        done,
    };

    return (
        <Pivot selectedKey={currentStep} styles={getPivotStyles}>
            <PivotItem itemKey={PreSetupTemplateSteps.None}>
                <LoadingPage />
            </PivotItem>
            <PivotItem itemKey={PreSetupTemplateSteps.BrowseTemplates}>
                <TemplateStore {...stepProps} />
            </PivotItem>
            <PivotItem itemKey={PreSetupTemplateSteps.PreviewTemplate}>
                <PreviewTemplate {...stepProps} />
            </PivotItem>
            <PivotItem itemKey={PreSetupTemplateSteps.SelectScope}>
                <ViewScope {...stepProps} />
            </PivotItem>
            <PivotItem itemKey={PreSetupTemplateSteps.SelectTeam}>
                <ViewTeam {...stepProps} />
            </PivotItem>
            <PivotItem itemKey={PreSetupTemplateSteps.CreateAdminGroup}>
                <CreateAdminGroup {...stepProps} />
            </PivotItem>
        </Pivot>
    );
};
